import { generateActionDeck, generateDuckDeck, shuffleArray } from "./decksBuilder.js";
import { calculatePlayableCards } from "./actionsChecker.js";
import { TEAMS } from "@/game-rules/constants/teams.js";
import { GAME_EVENTS } from "@/game-rules/constants/gameEvents.js";
import { GAME_PHASES } from "@/game-rules/constants/gamePhases.js";
import { lineOfFireSlotFactory } from "@/game-rules/lineOfFireSlot.js";

export class GameState extends EventTarget {
    constructor(config) {
        super();

        // Core State
        this.phase = GAME_PHASES.INIT; // Phases: INIT, PLAYER_TURN, RESOLUTION, GAME_OVER
        this.players = [];
        this.activeTeams = [];
        this.currentPlayerIndex = 0;
        this.selectedCardIndex = null;

        // Game rules
        this.gameMode = config.mode; // "HOTSEAT" or "VS_BOT"
        this.totalPlayers = config.players;
        this.humanColorId = config.humanColorId || null;

        // Action cards piles
        this.actionDeck = [];
        this.actionDiscardPile = [];

        // Duck cards piles
        this.duckDeck = [];
        this.duckDiscardPile = [];

        // Line of fire & targets
        this.lineOfFire = [];
        this.lineOfFireSlots = lineOfFireSlotFactory();

        // Trigger the master setup function
        this.runSetupSequence();
    }

    // ==========================================
    // SETUP & INITIALIZATION
    // ==========================================

    runSetupSequence() {
        this.initializePlayers();
        this.initDecks();
        this.initLineOfFire();
        this.determineFirstPlayer();
        this.dealInitialHands();
    }

    initializePlayers() {
        let availableColors = [...TEAMS];

        for (let i = 0; i < this.totalPlayers; i++) {
            let playerTeam;
            let isBot = false;

            // Player 1 is ALWAYS the human in VS_BOT mode
            if (i === 0 && this.gameMode === "VS_BOT") {
                isBot = false;
                playerTeam = availableColors.find(t => t.id === this.humanColorId);
                availableColors = availableColors.filter(t => t.id !== this.humanColorId);
            } else {
                isBot = this.gameMode === "VS_BOT";
                playerTeam = availableColors.shift();
            }

            this.activeTeams.push(playerTeam);

            // Create the player object
            this.players.push({
                id: i,
                team: playerTeam,
                isBot: isBot,
                hand: [],
                ducksAlive: 5,
                isEliminated: false
            });
        }
    }

    initDecks() {
        this.actionDeck = generateActionDeck();
        this.duckDeck = generateDuckDeck(this.activeTeams);
    }

    initLineOfFire() {
        this.lineOfFire = this.duckDeck.splice(0, 6);
    }

    determineFirstPlayer() {
        const firstDuck = this.lineOfFire.find(card => card.typeId !== "WATER");
        let startingTeamId = this.players[0].team.id;

        if (firstDuck) {
            startingTeamId = firstDuck.team.id;
        }

        this.currentPlayerIndex = this.players.findIndex(p => p.team.id === startingTeamId);
        console.log(`The leftmost duck is ${startingTeamId}. Player ${this.currentPlayerIndex} goes first!`);
    }

    dealInitialHands() {
        this.players.forEach(player => {
            for (let i = 0; i < 3; i++) {
                player.hand.push(this.drawActionCard());
            }
        });
    }

    // ==========================================
    // TURN MANAGEMENT
    // ==========================================

    startGame() {
        this.phase = GAME_PHASES.INIT;
        this.dispatchEvent(new CustomEvent(GAME_EVENTS.GAME_STARTED));
    }

    startInitialTurn() {
        this.phase = GAME_PHASES.PLAYER_TURN;

        this.dispatchEvent(new CustomEvent(GAME_EVENTS.TURN_STARTED));
    }

    endTurn() {
        this.selectedCardIndex = null;
        this.currentPlayerIndex = (this.currentPlayerIndex + 1) % this.totalPlayers;
        this.phase = GAME_PHASES.PLAYER_TURN;

        this.lineOfFireSlots.forEach((lineOfFireSlot, i) => {
            if (!lineOfFireSlot.isProtected()) return;
            lineOfFireSlot.protectionTurns--;

            if (!lineOfFireSlot.isProtected()) {
                this.dispatchEvent(new CustomEvent(GAME_EVENTS.EXPOSE_DUCK, {
                    detail: { lineOfFireSlotsIndex: i }
                }));
            }
        });

        this.dispatchEvent(new CustomEvent(GAME_EVENTS.TURN_STARTED));

        console.log(`Turn passed to Team ${this.getCurrentPlayerTeamId()}`);
    }

    playSelectedCard(targetIndex) {
        if (this.phase !== GAME_PHASES.PLAYER_TURN || this.selectedCardIndex === null) return;

        this.phase = GAME_PHASES.RESOLUTION;
        const playedCardIndex = this.selectedCardIndex;

        // Logic: Remove and Discard
        const playedCard = this.getCurrentPlayer().hand.splice(this.selectedCardIndex, 1)[0];
        this.actionDiscardPile.push(playedCard);
        this.selectedCardIndex = null;

        this.dispatchEvent(new CustomEvent(GAME_EVENTS.CARD_SELECTED, {
            detail: { selectedIndex: null }
        }));

        // Dispatch the event so the UI can animate the card
        this.dispatchEvent(new CustomEvent(GAME_EVENTS.CARD_PLAYED, {
            detail: {
                index: playedCardIndex,
                targetIndex: targetIndex
            }
        }));

        playedCard.resolve(targetIndex, this);
    }

    checkWinner() {
        // Get players who still have ducks alive (less than 5 dead)
        const alivePlayers = this.players.filter(player => {
            const deadCount = this.duckDiscardPile.filter(d =>
                d.team && d.team.id === player.team.id
            ).length;
            return deadCount < 5;
        });

        console.log('Alive player', alivePlayers);
        // Return the winner object if only one player remains
        if (alivePlayers.length === 1) {
            return alivePlayers[0];
        }

        // Otherwise return null
        return null;
    }

    // ==========================================
    // DECK & CARD MANAGEMENT
    // ==========================================

    drawActionCard() {
        if (this.actionDeck.length === 0) {
            this.actionDeck = shuffleArray(this.actionDiscardPile);
            this.actionDiscardPile = [];
        }

        return this.actionDeck.pop();
    }

    drawActionCardManager() {
        const activePlayer = this.getCurrentPlayer();
        activePlayer.hand.push(this.drawActionCard());

        this.dispatchEvent(new CustomEvent(GAME_EVENTS.CARD_DRAWN, {
            detail: {
                playerIndex: this.currentPlayerIndex,
                newCard: activePlayer.hand[activePlayer.hand.length - 1]
            }
        }));
    }

    selectCard(index) {
        if (this.phase !== GAME_PHASES.PLAYER_TURN) return;

        // If they click the card that is already selected, deselect it!
        if (this.selectedCardIndex === index) {
            this.deselectCard();
            return;
        }

        this.selectedCardIndex = index;
        console.log(`Card ${index} selected.`);

        this.dispatchEvent(new CustomEvent(GAME_EVENTS.CARD_SELECTED, {
            detail: { selectedIndex: index }
        }));
    }

    deselectCard() {
        if (this.selectedCardIndex === null) return;
        this.selectedCardIndex = null;

        this.dispatchEvent(new CustomEvent(GAME_EVENTS.CARD_SELECTED, {
            detail: { selectedIndex: null }
        }));
    }

    forceDiscard(cardIndex) {
        if (this.phase !== GAME_PHASES.PLAYER_TURN) return;

        // Remove the card from the hand and put it in the discard pile
        const discardedCard = this.getCurrentPlayer().hand.splice(cardIndex, 1)[0];
        this.actionDiscardPile.push(discardedCard);

        console.log(`Player forced to discard: ${discardedCard.name}`);
    }

    // ==========================================
    // BOARD & COMBAT LOGIC
    // ==========================================

    forwardMarch() {
        const lineOfFireItemOut = this.lineOfFire.splice(0, 1)[0];
        this.duckDeck.unshift(lineOfFireItemOut);
        this.lineOfFire.push(this.duckDeck.pop());

        this.dispatchEvent(new CustomEvent(GAME_EVENTS.FORWARD_MARCH, {}));
    }

    dispersion() {
        this.duckDeck.unshift(...this.lineOfFire);
        this.lineOfFire = [];
        this.duckDeck = shuffleArray(this.duckDeck);
        for (let i = 0; i < 6; i++) {
            this.lineOfFire.push(this.duckDeck.pop());
        }

        this.dispatchEvent(new CustomEvent(GAME_EVENTS.DISPERTION, {}));
    }

    banzai(duckIndex) {
        const duck = this.lineOfFire[duckIndex];

        if (duck.team?.id !== this.getCurrentPlayerTeamId()) {
            console.warn(`Duck ${duckIndex} isn't your duck !`);
            return;
        }

        if (duckIndex === 0) {
            console.warn(`Can't move duck on the first slot`);
            return;
        }

        const duckToMove = this.lineOfFire.splice(duckIndex, 1)[0];
        this.lineOfFire.unshift(duckToMove);

        this.dispatchEvent(new CustomEvent(GAME_EVENTS.BANZAI, {
            detail: {duckIndex: duckIndex}
        }));
    }

    resolveKills(slotsIndex, cardTypeId) {
        // Only allow index that actually exist on the board
        const validSlotsIndex = slotsIndex.filter(index => index >= 0 && index < this.lineOfFire.length);

        if (validSlotsIndex.length === 0) {
            console.warn("resolveKills called with no valid index on the board!", slotsIndex);
            return;
        }

        // Consume the target tokens (pass the sanitized array)
        this.removeTargetsFromSlots(validSlotsIndex);

        let killedCardsInfo = [];
        let waterHits = [];

        // Reorder to remove index from the end of the list safely
        const sortedSlotsIndex = [...validSlotsIndex].sort((a, b) => b - a);

        sortedSlotsIndex.forEach(index => {
            const targetCard = this.lineOfFire[index];
            if (!targetCard) return;

            // Water and protected duck are dummy target
            if (targetCard.typeId === "WATER" || this.slotIsProtected(index)) {
                waterHits.push(index);
            }
            else {
                // Kill the duck and remove it from the array
                const killedCard = this.lineOfFire.splice(index, 1)[0];
                this.duckDiscardPile.push(killedCard);

                // Save what died, and where was it
                killedCardsInfo.push({ indexKilled: index, card: killedCard });
            }
        });

        // Update the State's line of fire
        for (let i = 0; i < killedCardsInfo.length; i++) {
            if (this.duckDeck.length > 0) {
                this.lineOfFire.push(this.duckDeck.pop());
            }
        }

        // Dispatch the lean master event
        this.dispatchEvent(new CustomEvent(GAME_EVENTS.SHOT_RESOLUTION, {
            detail: {
                kills: killedCardsInfo,
                waterHits: waterHits,
                cardTypeId: cardTypeId
            }
        }));
    }

    adjustShot(targetIndex, side) {
        const direction = { left: -1, right: 1 }[side];
        const newIndex = targetIndex + direction;

        if (!this.lineOfFireSlots[targetIndex].isTargeted) {
            console.warn(`Slot ${targetIndex} isn't targeted!`);
            return;
        }

        if (this.lineOfFireSlots[newIndex].isTargeted) {
            console.warn(`Slot ${targetIndex} is already targeted!`);
            return;
        }

        this.lineOfFireSlots[targetIndex].isTargeted = false;
        this.lineOfFireSlots[newIndex].isTargeted = true;

        this.dispatchEvent(new CustomEvent(GAME_EVENTS.TARGET_REMOVED, {
            detail: { slotsIndex: [targetIndex] }
        }));

        this.dispatchEvent(new CustomEvent(GAME_EVENTS.TARGET_ADDED, {
            detail: { slotsIndex: [newIndex] }
        }));
    }

    swapDuck(duckIndex, side) {
        const direction = { left: -1, right: 1 }[side];
        const duck = this.lineOfFire[duckIndex];
        const newIndex = duckIndex + direction;

        if (duck.team?.id !== this.getCurrentPlayerTeamId()) {
            console.warn(`Duck ${duckIndex} isn't your duck !`);
            return;
        }

        if (side === "left" && duckIndex === 0) {
            console.warn(`Can't move duck to left on first slot`);
            return;
        }
        if (side === "right" && duckIndex === 5) {
            console.warn(`Can't move duck to right on last slot`);
            return;
        }

        [this.lineOfFire[duckIndex], this.lineOfFire[newIndex]] = [this.lineOfFire[newIndex], this.lineOfFire[duckIndex]];

        this.dispatchEvent(new CustomEvent(GAME_EVENTS.SWAP_DUCK, {
            detail: { duckFromIndex: duckIndex, duckToIndex: newIndex }
        }));
    }

    addTargetsToSlots(targetedSlotsIndex, preventDraw = false) {
        let successfullyPlacedTargetIndex = [];

        targetedSlotsIndex.forEach(index => {
            if (index >= 0 && index <= 5) {
                if (!this.lineOfFireSlots[index].isTargeted) {
                    this.lineOfFireSlots[index].isTargeted = true;
                    successfullyPlacedTargetIndex.push(index);
                }
            }
        });

        if (successfullyPlacedTargetIndex.length > 0) {
            this.dispatchEvent(new CustomEvent(GAME_EVENTS.TARGET_ADDED, {
                detail: {
                    slotsIndex: successfullyPlacedTargetIndex,
                    preventDraw: preventDraw
                }
            }));
        } else {
            console.warn("addTargetsToSlots called with no target to place")
        }
    }

    // Remove mutliple target token
    removeTargetsFromSlots(slotsIndex) {
        let successfullyRemovedTarget = [];

        slotsIndex.forEach(index => {
            if (index >= 0 && index <= 5) {
                if (this.lineOfFireSlots[index].isTargeted) {
                    this.lineOfFireSlots[index].isTargeted = false;
                    successfullyRemovedTarget.push(index);
                }
            }
        });

        // If we removed at least one target, tell the scene
        if (successfullyRemovedTarget.length > 0) {
            this.dispatchEvent(new CustomEvent(GAME_EVENTS.TARGET_REMOVED, {
                detail: { slotsIndex: successfullyRemovedTarget }
            }));
        } else {
            console.warn("removeTargetsFromSlots called without target to removed")
        }
    }

    planque(index) {
        this.lineOfFireSlots[index].protectionTurns = this.players.length;

        this.dispatchEvent(new CustomEvent("protect_duck", {
            detail: { slotIndex: index }
        }));
    }

    // ==========================================
    // BOARD & COMBAT LOGIC HELPER
    // ==========================================
    getPlayableCard() {
        return calculatePlayableCards(this);
    }

    // ==========================================
    // STATE HELPER
    // ==========================================
    getCurrentPlayer() {
        return this.players[this.currentPlayerIndex];
    }

    getCurrentPlayerTeamId() {
        return this.getCurrentPlayer().team.id;
    }

    slotIsProtected(index) {
        return this.lineOfFireSlots[index].isProtected();
    }

    // ==========================================
    // CHEATS & DEBUG TOOLS
    // ==========================================
    cheatTargetsOnEverySlots() {
        this.addTargetsToSlots([0, 1, 2, 3, 4, 5], true);
    }
}